import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Controls'
import { ErrorBox, Loading } from '../components/common/Feedback'
import { LoginFrame } from './LoginPage'
import s from './OAuthLoginPage.module.scss'

/**
 * Die Anmeldung des OAuth-Ablaufs.
 *
 * DIESELBE VIEW wie die gewöhnliche Anmeldung — `LoginFrame` trägt Karte,
 * Logo und Schriftzug. Was hier dazukommt, ist der Grund: WELCHE Verbindung
 * WELCHE Rechte verlangt. Bei der gewöhnlichen Anmeldung gibt es das nicht,
 * weil man sich dort bei der eigenen Anwendung anmeldet.
 *
 * KEIN `fetch` BEIM ABSENDEN. Das Formular geht als gewöhnlicher POST an den
 * Server, weil darauf eine Weiterleitung zurück zum Client folgt — der muss
 * der Browser folgen können. Ein `fetch` bekäme die Weiterleitung und täte
 * nichts damit.
 *
 * Der Vorgang selbst — Name der Verbindung, Rechte, CSRF-Token — kommt über
 * `…/login/request`. Er steht nicht im HTML, damit die ausgelieferte
 * index.html dieselbe Datei bleibt wie für jede andere Seite.
 */

interface Recht {
  scope: string
  title: string
  text: string
}

interface Vorgang {
  valid: boolean
  client: string
  csrf: string
  rights: Recht[]
}

/** Die Meldung zu dem Grund, der in der Adresse steht. */
function fehlertext(grund: string | null, text: string | null): string | null {
  if (!grund) return null
  if (grund === 'credentials') return 'Benutzer oder Kennwort ist falsch.'
  if (grund === 'rights') {
    return text ?? 'Für die verlangten Rechte fehlt die Berechtigung.'
  }
  return text ?? 'Die Anmeldung ist fehlgeschlagen.'
}

export function OAuthLoginPage() {
  const [params] = useSearchParams()
  const req = params.get('req') ?? ''
  const [vorgang, setVorgang] = useState<Vorgang | null>(null)
  const [laedt, setLaedt] = useState(true)
  const [busy, setBusy] = useState(false)

  const fehler = fehlertext(params.get('fehler'), params.get('text'))

  useEffect(() => {
    let abgebrochen = false
    // Der eigene Pfad ohne die Abfrageparameter, plus `/request`. Fest
    // verdrahtet wäre er eine zweite Stelle, die beim Verschieben vergessen
    // wird.
    const ziel = `${window.location.pathname}/request?req=${encodeURIComponent(req)}`
    fetch(ziel, { headers: { Accept: 'application/json' } })
      .then((antwort) => antwort.ok ? antwort.json() : { valid: false })
      .then((daten) => { if (!abgebrochen) { setVorgang(daten); setLaedt(false) } })
      .catch(() => { if (!abgebrochen) { setVorgang(null); setLaedt(false) } })
    return () => { abgebrochen = true }
  }, [req])

  if (laedt) {
    return <LoginFrame><Loading /></LoginFrame>
  }

  if (!vorgang?.valid) {
    return (
      <LoginFrame>
        <h1 className={s.titel}>Anfrage nicht mehr gültig</h1>
        <p className={s.hinweis}>
          Der Vorgang ist abgelaufen. Bitte im Client erneut verbinden.
        </p>
      </LoginFrame>
    )
  }

  return (
    <LoginFrame method="post" onSubmit={() => setBusy(true)}>
      <h1 className={s.titel}>Zugriff bestätigen</h1>
      <p className={s.hinweis}>
        Eine Verbindung möchte in Ihrem Namen auf Instrument Watcher zugreifen.
      </p>

      <div className={s.karte}>
        <dl className={s.liste}>
          <dt>Verbindung</dt>
          <dd>{vorgang.client}</dd>
          {vorgang.rights.length === 0 ? (
            <>
              <dt>Rechte</dt>
              <dd>alles, was Ihr Konto darf</dd>
            </>
          ) : vorgang.rights.map((r) => (
            <div key={r.scope} className={s.recht}>
              <dt>{r.title}</dt>
              <dd>{r.text}</dd>
            </div>
          ))}
        </dl>
      </div>

      {/* Die Zieladresse kommt NICHT aus dem Formular — sie steht im
          zwischengelegten Zustand des Servers. Hier reisen nur Vorgang und
          CSRF-Token mit. */}
      <input type="hidden" name="req" value={req} />
      <input type="hidden" name="csrf" value={vorgang.csrf} />

      {/* NICHT `disabled` waehrend des Absendens.

          Ein deaktiviertes Feld sendet der Browser NICHT mit. `onSubmit`
          setzt `busy`, React rendert neu — und noch bevor die Anfrage
          hinausgeht, sind Benutzer und Kennwort leer. Der Server bekommt
          nichts und antwortet voellig richtig mit "Benutzer oder Kennwort ist
          falsch".

          Nur der Knopf wird gesperrt; das reicht gegen doppeltes Abschicken
          und laesst die Werte stehen. `readOnly` waere die Alternative — es
          sendet mit —, aber ein Formular, das ohnehin gleich die Seite
          verlaesst, braucht es nicht. */}
      <Input label="Benutzer" name="user" autoFocus autoComplete="username"
             required />
      <Input label="Kennwort" name="password" type="password"
             autoComplete="current-password" required />

      {fehler && <ErrorBox>{fehler}</ErrorBox>}

      <Button type="submit" variant="primary" block disabled={busy}>
        {busy ? 'prüfe …' : 'Bestätigen'}
      </Button>

      <p className={s.fuss}>
        Dieselben Zugangsdaten wie für die Oberfläche. Was über diese
        Verbindung eingetragen wird, trägt Ihren Namen.
      </p>
    </LoginFrame>
  )
}
